/**
 * Pure calculation engines used by BudgetOS UI hooks AND the automated test suite.
 * Keep all money math here — never duplicate formulas in hooks or tests.
 */

import type {
  Category,
  CategorySummary,
  Debt,
  DebtResult,
  DebtScheduleRow,
  MonthlySummary,
  NetWorthLog,
  SinkingFund,
  Transaction,
} from "./schema";

export interface BudgetFilters {
  person?: string;
  year?: number;
}

export function applyFilters(txs: Transaction[], f: BudgetFilters): Transaction[] {
  let result = txs.filter((t) => !t.skipped);
  if (f.person && f.person !== "All") {
    result = result.filter((t) => t.person === f.person);
  }
  if (f.year) {
    result = result.filter((t) => t.date.startsWith(String(f.year)));
  }
  return result;
}

export interface DashboardSummary {
  income: number;
  expense: number;
  leftover: number;
  savingsRate: number;
  actualSaved: number;
  totalSavedTarget: number;
}

export function computeDashboardSummary(
  txs: Transaction[],
  categories: Category[]
): DashboardSummary {
  const income = txs.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
  const expense = txs.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
  const leftover = income - expense;
  const savingsRate = income > 0 ? (leftover / income) * 100 : 0;

  const savedCategories = categories.filter((c) => c.group === "savings");
  const totalSavedTarget = savedCategories.reduce((s, c) => s + c.monthlyTarget * 12, 0);
  const actualSaved = txs
    .filter((t) => t.type === "expense" && savedCategories.some((c) => c.id === t.categoryId))
    .reduce((s, t) => s + t.amount, 0);

  return { income, expense, leftover, savingsRate, actualSaved, totalSavedTarget };
}

export function computeMonthlyBreakdown(txs: Transaction[], year: number): MonthlySummary[] {
  return Array.from({ length: 12 }, (_, i) => {
    const m = String(i + 1).padStart(2, "0");
    const prefix = `${year}-${m}`;
    const monthTxs = txs.filter((t) => t.date.startsWith(prefix));
    const totalIncome = monthTxs.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
    const totalExpense = monthTxs.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
    const leftover = totalIncome - totalExpense;
    const savingsRate = totalIncome > 0 ? (leftover / totalIncome) * 100 : 0;
    return { month: i + 1, year, totalIncome, totalExpense, leftover, savingsRate };
  });
}

export function computeMonthCategorySummary(
  allTxs: Transaction[],
  categories: Category[],
  year: number,
  month: number
): { income: CategorySummary[]; expense: CategorySummary[] } {
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  const txs = allTxs.filter((t) => t.date.startsWith(prefix));

  const summarize = (cats: Category[]): CategorySummary[] =>
    cats.map((cat) => {
      const actual = txs
        .filter((t) => t.categoryId === cat.id)
        .reduce((s, t) => s + t.amount, 0);
      const variance = cat.monthlyTarget - actual;
      const pct = cat.monthlyTarget > 0 ? (actual / cat.monthlyTarget) * 100 : 0;
      return { ...cat, actual, variance, pct };
    });

  return {
    income: summarize(categories.filter((c) => c.type === "income")),
    expense: summarize(categories.filter((c) => c.type === "expense")),
  };
}

export interface Rule503020Result {
  income: number;
  needs: number;
  needsPct: number;
  needsTarget: number;
  wants: number;
  wantsPct: number;
  wantsTarget: number;
  savings: number;
  savingsPct: number;
  savingsTarget: number;
}

export function compute503020(
  allTxs: Transaction[],
  categories: Category[],
  year: number,
  month: number
): Rule503020Result {
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  const txs = allTxs.filter((t) => t.date.startsWith(prefix) && t.type === "expense");
  const income = allTxs
    .filter((t) => t.date.startsWith(prefix) && t.type === "income")
    .reduce((s, t) => s + t.amount, 0);

  const amountFor = (group: string) =>
    txs
      .filter((t) => categories.find((c) => c.id === t.categoryId)?.group === group)
      .reduce((s, t) => s + t.amount, 0);

  const needs = amountFor("needs");
  const wants = amountFor("wants");
  const savings = amountFor("savings");
  const pct = (v: number) => (income > 0 ? (v / income) * 100 : 0);

  return {
    income,
    needs,
    needsPct: pct(needs),
    needsTarget: 50,
    wants,
    wantsPct: pct(wants),
    wantsTarget: 30,
    savings,
    savingsPct: pct(savings),
    savingsTarget: 20,
  };
}

export interface CalendarStats {
  byDay: Record<number, number>;
  streak: number;
  noSpendDays: number;
  spendDays: number;
  totalSpend: number;
  avgPerDay: number;
}

export function computeSpendingCalendar(
  allTxs: Transaction[],
  year: number,
  month: number,
  /** Optional "as of" day for streak (defaults to today if same month, else last day of month) */
  asOfDay?: number
): CalendarStats {
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  const txs = allTxs.filter((t) => t.date.startsWith(prefix) && t.type === "expense");

  const byDay: Record<number, number> = {};
  txs.forEach((t) => {
    const day = parseInt(t.date.split("-")[2], 10);
    byDay[day] = (byDay[day] ?? 0) + t.amount;
  });

  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const startDay =
    asOfDay ??
    (today.getFullYear() === year && today.getMonth() + 1 === month
      ? today.getDate()
      : daysInMonth);

  let streak = 0;
  for (let d = startDay; d >= 1; d--) {
    if (!byDay[d] || byDay[d] === 0) streak++;
    else break;
  }

  let noSpendDays = 0;
  for (let d = 1; d <= startDay; d++) {
    if (!byDay[d]) noSpendDays++;
  }
  const spendDays = startDay - noSpendDays;
  const totalSpend = Object.values(byDay).reduce((s, v) => s + v, 0);
  const avgPerDay = spendDays > 0 ? totalSpend / spendDays : 0;

  return { byDay, streak, noSpendDays, spendDays, totalSpend, avgPerDay };
}

export function computeDebtPayoff(
  debts: Debt[],
  method: "snowball" | "avalanche" = "snowball",
  /** Freeze payoff start date for deterministic tests */
  startDate: Date = new Date()
): DebtResult[] {
  if (!debts.length) return [];

  const sorted = [...debts].sort((a, b) =>
    method === "snowball" ? a.balance - b.balance : b.interestRate - a.interestRate
  );

  let cascadeExtra = 0;

  return sorted.map((debt) => {
    const monthlyRate = debt.interestRate / 100 / 12;
    const payment = debt.minPayment + debt.extraPayment + cascadeExtra;
    const schedule: DebtScheduleRow[] = [];
    let balance = debt.balance;
    let totalInterest = 0;
    let month = 0;

    while (balance > 0 && month < 600) {
      const interest = balance * monthlyRate;
      const principal = Math.min(payment - interest, balance);
      totalInterest += interest;
      balance = Math.max(0, balance - principal);
      month++;
      schedule.push({
        month,
        balance: Math.round(balance * 100) / 100,
        payment: Math.round(Math.min(payment, balance + principal + interest) * 100) / 100,
        interest: Math.round(interest * 100) / 100,
        principal: Math.round(principal * 100) / 100,
      });
    }

    const payoffDate = new Date(startDate);
    payoffDate.setMonth(payoffDate.getMonth() + month);
    cascadeExtra += debt.minPayment;

    return {
      debt,
      schedule,
      totalInterest: Math.round(totalInterest * 100) / 100,
      payoffDate,
      monthsToPayoff: month,
    };
  });
}

export interface SinkingFundStats {
  pct: number;
  remaining: number;
  monthsLeft: number;
  monthlyNeeded: number;
  overfunded: boolean;
}

export function computeSinkingFundStats(
  fund: SinkingFund,
  asOf: Date = new Date()
): SinkingFundStats {
  const target = new Date(fund.targetDate);
  const monthsLeft = Math.max(
    1,
    (target.getFullYear() - asOf.getFullYear()) * 12 + (target.getMonth() - asOf.getMonth())
  );
  const remaining = fund.targetAmount - fund.currentSaved;
  const pct = fund.targetAmount > 0 ? (fund.currentSaved / fund.targetAmount) * 100 : 0;
  const monthlyNeeded = Math.max(0, remaining / monthsLeft);
  return {
    pct,
    remaining,
    monthsLeft,
    monthlyNeeded,
    overfunded: fund.currentSaved > fund.targetAmount,
  };
}

export interface NetWorthStats {
  netWorth: number;
  assets: number;
  debts: number;
  debtRatio: number;
  momDelta: number | null;
}

export function computeNetWorthStats(logs: NetWorthLog[]): NetWorthStats {
  if (!logs.length) {
    return { netWorth: 0, assets: 0, debts: 0, debtRatio: 0, momDelta: null };
  }
  const sorted = [...logs].sort((a, b) => a.monthYear.localeCompare(b.monthYear));
  const latest = sorted[sorted.length - 1];
  const prev = sorted.length > 1 ? sorted[sorted.length - 2] : null;
  const netWorth = latest.assetsTotal - latest.debtsTotal;
  const prevNw = prev ? prev.assetsTotal - prev.debtsTotal : null;
  const debtRatio =
    latest.assetsTotal > 0 ? (latest.debtsTotal / latest.assetsTotal) * 100 : 0;

  return {
    netWorth,
    assets: latest.assetsTotal,
    debts: latest.debtsTotal,
    debtRatio,
    momDelta: prevNw === null ? null : netWorth - prevNw,
  };
}

/** Format currency the same way useCurrency does — raw amounts must stay identical across locales. */
export function formatCurrencyAmount(
  amount: number,
  currency: string,
  locale: string,
  compact = false
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    notation: compact ? "compact" : "standard",
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits: compact ? 1 : 2,
  }).format(amount);
}

/** Round money to cents for equality checks */
export function moneyEq(a: number, b: number, eps = 0.005): boolean {
  return Math.abs(a - b) < eps;
}

export function pctEq(a: number, b: number, eps = 0.01): boolean {
  return Math.abs(a - b) < eps;
}
